import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Form } from "react-bootstrap";
import { AddUsersWrapper } from "./styled";
import {
  BackButton,
  Box,
  ButtonStyled,
  TabsList,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import { userFormTabs } from "../../../helpers/adminConstants/AdminConstants";
import { ContentWrapperWithoutYMargin } from "../../../components/styled/styled";
import {
  LeftArrowBlack,
  RightArrowWhite,
  RightArrowWithoutBar,
} from "../../../components/styled/AllImages";
import { toast } from "react-toastify";
import UsersApi from "../../../services/api/usersApi";

const AddUsers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const controllerRef = useRef(null);
  const {
    register,
    control,
    handleSubmit,
    trigger,
    watch,
    resetField,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });
  const permissionsObj = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
  };

  const [activeTab, setActiveTab] = useState(userFormTabs[0]);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState(permissionsObj);
  const currentTabIndex = userFormTabs.findIndex(
    (tab) => tab.id === activeTab.id
  );
  const handleTabChange = async (tabIndex) => {
    const isValid = await trigger(activeTab.fields);
    if (isValid) {
      setActiveTab(userFormTabs[tabIndex]);
    }
  };

  const handleNext = async () => {
    const isValid = await trigger(activeTab.fields);
    if (isValid) setActiveTab(userFormTabs[currentTabIndex + 1]);
  };

  const onSubmit = async (data) => {
    controllerRef.current = new AbortController();
    const signal = controllerRef?.current?.signal;
    const permissionsObj = Object.fromEntries(
      Object.entries(permissions).map(([key, value]) => [
        key,
        value.filter((perm) => perm !== "all"),
      ])
    );

    const hasSelectedPermissions = Object.values(permissionsObj).some(
      (perms) => perms.length > 0
    );

    if (!hasSelectedPermissions) {
      toast.error(t("please_select_permission"));
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      formData.append("action", "create");
      formData.append("permissions", JSON.stringify(permissionsObj));

      const { code, message, error } = await UsersApi.createUser(
        formData,
        signal
      );
      if (code === 200) {
        toast.success(message);
        navigate("/users");
      }
      if (code === 204 || code === 100) {
        toast.error(message);
      }
      if (code === 500 && error) {
        toast.error(error);
      }
      if (code === 422 && message) {
        toast.error(message);
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        toast.info(t("request_cancelled_successfully"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AddUsersWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("create_user")}
            onClick={() => navigate("/users")}
          />
          <Breadcrumbs />
        </Box>
      </Box>

      <Box className="tabs-header-wrapper">
        <TabsList
          tabs={userFormTabs}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
        />
      </Box>
      <ContentWrapperWithoutYMargin>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <activeTab.component
            register={register}
            control={control}
            errors={errors}
            watch={watch}
            resetField={resetField}
            setValue={setValue}
            permissions={permissions}
            setPermissions={setPermissions}
          />
          <Box className="d-flex flex-column-reverse flex-sm-row justify-content-between mx-3 py-4 gap-3">
            <ButtonStyled
              iconBefore={LeftArrowBlack}
              label={t("back")}
              border="var(--borderExtraLight)"
              color="var(--text)"
              bg="transparent"
              type="button"
              onClick={() => setActiveTab(userFormTabs[0])}
              disabled={activeTab === userFormTabs[0]}
            />
            {activeTab === userFormTabs[0] && (
              <Box className="d-flex align-items-center gap-3">
                <ButtonStyled
                  border="var(--borderExtraLight)"
                  iconBefore={LeftArrowBlack}
                  bg="transparent"
                  label={t("Cancel")}
                  color="var(--text)"
                  type="button"
                  disabled
                />
                <ButtonStyled
                  label={t("next")}
                  icon={RightArrowWhite}
                  type="button"
                  bg="var(--themeColor)"
                  onClick={handleNext}
                />
              </Box>
            )}
            {activeTab === userFormTabs[1] && (
              <Box className="d-flex align-items-center gap-3">
                <ButtonStyled
                  border="var(--borderExtraLight)"
                  iconBefore={LeftArrowBlack}
                  bg="transparent"
                  label={t("Cancel")}
                  color="var(--text)"
                  type="button"
                  onClick={() => {
                    if (controllerRef.current) {
                      controllerRef.current.abort();
                      toast.info(t("request_cancelled_successfully"));
                    }
                  }}
                />
                <ButtonStyled
                  icon={RightArrowWhite}
                  type="submit"
                  label={t("create")}
                  isLoading={loading}
                  bg="var(--themeColor)"
                />
              </Box>
            )}
          </Box>
        </Form>
      </ContentWrapperWithoutYMargin>
    </AddUsersWrapper>
  );
};

export default AddUsers;
